import React, { useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';

export interface DrawingCanvasRef {
  clear: () => void;
  getPngBase64: () => string | null;
  loadBase64: (base64: string) => void;
}

export interface DrawingCanvasProps {
  width: number;
  height: number;
  color: string;
  brushSize: number;
  /** Optional guide image URL shown faint under the drawing */
  backgroundImageUrl?: string | null;
  /** Optional guide text (e.g. target character) shown faint when no image */
  backgroundText?: string | null;
  className?: string;
}

export const DrawingCanvas = forwardRef<DrawingCanvasRef, DrawingCanvasProps>(
  ({ width, height, color, brushSize, backgroundImageUrl, backgroundText, className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDrawing = useRef(false);
    const lastPoint = useRef<{ x: number; y: number } | null>(null);
    const [guideImageFailed, setGuideImageFailed] = React.useState(false);

    React.    useEffect(() => {
      setGuideImageFailed(false);
    }, [backgroundImageUrl]);

    const getCtx = useCallback(() => canvasRef.current?.getContext('2d') ?? null, []);

    const getPoint = useCallback(
      (e: React.PointerEvent | PointerEvent): { x: number; y: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const scaleX = width / rect.width;
        const scaleY = height / rect.height;
        const clientX = 'clientX' in e ? e.clientX : (e as unknown as { touches?: { clientX: number; clientY: number }[] }).touches?.[0]?.clientX ?? 0;
        const clientY = 'clientY' in e ? e.clientY : (e as unknown as { touches?: { clientX: number; clientY: number }[] }).touches?.[0]?.clientY ?? 0;
        return {
          x: (clientX - rect.left) * scaleX,
          y: (clientY - rect.top) * scaleY,
        };
      },
      [width, height]
    );

    const drawLine = useCallback(
      (from: { x: number; y: number }, to: { x: number; y: number }) => {
        const ctx = getCtx();
        if (!ctx) return;
        ctx.strokeStyle = color;
        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      },
      [color, brushSize, getCtx]
    );

    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.preventDefault();
        const pt = getPoint(e);
        if (pt) {
          isDrawing.current = true;
          lastPoint.current = pt;
          const ctx = getCtx();
          if (ctx) {
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, brushSize / 2, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
          }
        }
      },
      [getPoint, getCtx, color, brushSize]
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!isDrawing.current) return;
        e.preventDefault();
        const pt = getPoint(e);
        const last = lastPoint.current;
        if (pt && last) {
          drawLine(last, pt);
          lastPoint.current = pt;
        }
      },
      [getPoint, drawLine]
    );

    const handlePointerUp = useCallback(() => {
      isDrawing.current = false;
      lastPoint.current = null;
    }, []);

    const handlePointerLeave = useCallback(() => {
      isDrawing.current = false;
      lastPoint.current = null;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        clear() {
          const canvas = canvasRef.current;
          const ctx = getCtx();
          if (canvas && ctx) ctx.clearRect(0, 0, width, height);
        },
        getPngBase64() {
          const canvas = canvasRef.current;
          if (!canvas) return null;
          try {
            return canvas.toDataURL('image/png');
          } catch {
            return null;
          }
        },
        loadBase64(base64: string) {
          const canvas = canvasRef.current;
          const ctx = getCtx();
          if (!canvas || !ctx) return;
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              // clear existing strokes then draw the image into the canvas
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(img, 0, 0, width, height);
            };
            img.src = base64;
          } catch {
            // ignore
          }
        },
      }),
      [getCtx, width, height]
    );

    return (
      <div className={className} style={{ position: 'relative' }}>
        {/* Guide layer: faint image or text under the canvas. Image onError falls back to text. */}
        <div
          className="absolute inset-0 rounded-lg border-2 border-gray-200 dark:border-gray-700 overflow-hidden flex items-center justify-center bg-white dark:bg-gray-900"
          style={{ width, height, maxWidth: '100%' }}
          aria-hidden
        >
          {backgroundImageUrl && !guideImageFailed ? (
            <img
              src={backgroundImageUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="max-w-full max-h-full object-contain opacity-25"
              onError={() => setGuideImageFailed(true)}
            />
          ) : backgroundText ? (
            <span className="text-gray-400 dark:text-gray-500 opacity-30 text-8xl font-serif">
              {backgroundText}
            </span>
          ) : null}
        </div>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="touch-none cursor-crosshair block relative z-10 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-transparent"
          style={{ maxWidth: '100%', height: 'auto' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onPointerCancel={handlePointerUp}
        />
      </div>
    );
  }
);

DrawingCanvas.displayName = 'DrawingCanvas';
